"use client";

import {
  useSyncExternalStore,
  type ComponentPropsWithoutRef,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from "react";

import { iconAxes } from "./icon-axes";
import { useIconSet, type IconSet } from "./icon-set";
import type { IconName } from "./icon-names";
import { phosphorNameFor } from "./material-to-phosphor";
import { PHOSPHOR_BY_ESCAPE, PHOSPHOR_BY_MATERIAL } from "./phosphor-registry";
import { phosphorWeight } from "./phosphor-weight";
import { cn } from "./utils";

export const ICON_SIZE_PX = {
  micro: 10,
  xs: 12,
  sm: 14,
  md: 16,
  lg: 20,
  xl: 24,
  "2xl": 32,
  "3xl": 40,
  "4xl": 48,
} as const;

export type IconSize = keyof typeof ICON_SIZE_PX;

/** Catalog name, or any Google ligature (Storybook / docs escape hatch). */
export type IconLigature = IconName | (string & {});

export type IconProps = Omit<ComponentPropsWithoutRef<"span">, "children"> & {
  name: IconLigature;
  /** Named ladder or CSS px. Axes (opsz / wght / GRAD) always come from `iconAxes`. */
  size?: IconSize | number;
  /** FILL 0|1. Outlined chrome by default; filled = selected / on / presence. */
  filled?: boolean;
  /** Accessible name. Omit for decorative icons (`aria-hidden`). */
  label?: string;
  /**
   * Renderer family. Omit to inherit `IconSetProvider` (default `material`).
   * Catalog `name` does not change when the set does.
   */
  set?: IconSet;
};

let darkCached = false;
const darkListeners = new Set<() => void>();
let darkObserver: MutationObserver | null = null;

function readDark() {
  return typeof document !== "undefined" && document.documentElement.classList.contains("dark");
}

function subscribeDark(onStoreChange: () => void) {
  darkListeners.add(onStoreChange);
  if (!darkObserver && typeof MutationObserver !== "undefined" && typeof document !== "undefined") {
    darkCached = readDark();
    darkObserver = new MutationObserver(() => {
      const next = readDark();
      if (next === darkCached) return;
      darkCached = next;
      darkListeners.forEach((listener) => listener());
    });
    darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  }
  return () => {
    darkListeners.delete(onStoreChange);
  };
}

function useDarkClass() {
  return useSyncExternalStore(subscribeDark, readDark, () => false);
}

function resolvePx(size: IconSize | number | undefined) {
  if (typeof size === "number") return size;
  return ICON_SIZE_PX[size ?? "md"];
}

type BoxProps = Omit<IconProps, "name" | "size" | "filled" | "label" | "set">;

function IconBox({
  label,
  className,
  style,
  set,
  fallback,
  children,
  ...rest
}: BoxProps & {
  label?: string;
  set: IconSet;
  fallback?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      data-slot="icon"
      data-icon-set={set}
      data-icon-fallback={fallback ? "material" : undefined}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn("inline-block shrink-0 overflow-hidden align-middle select-none", className)}
      style={style}
      {...rest}
    >
      {children}
    </span>
  );
}

function MaterialGlyph({
  name,
  px,
  filled,
  dark,
  label,
  className,
  style,
  set,
  fallback,
  ...rest
}: BoxProps & {
  name: string;
  px: number;
  filled: boolean;
  dark: boolean;
  label?: string;
  set: IconSet;
  fallback?: boolean;
}) {
  const { opsz, wght, GRAD } = iconAxes(px, { dark });
  return (
    <IconBox
      label={label}
      className={className}
      set={set}
      fallback={fallback}
      style={{
        fontFamily: 'var(--font-icon, "Material Symbols Outlined Variable")',
        fontSize: px,
        fontStyle: "normal",
        fontWeight: wght,
        width: "1em",
        height: "1em",
        lineHeight: 1,
        letterSpacing: "normal",
        textTransform: "none",
        whiteSpace: "nowrap",
        direction: "ltr",
        fontFeatureSettings: '"liga"',
        fontVariationSettings: `"FILL" ${filled ? 1 : 0}, "wght" ${wght}, "GRAD" ${GRAD}, "opsz" ${opsz}`,
        WebkitFontSmoothing: "antialiased",
        ...style,
      }}
      {...rest}
    >
      {name}
    </IconBox>
  );
}

function phosphorComponent(ligature: string) {
  if (ligature in PHOSPHOR_BY_MATERIAL) {
    return PHOSPHOR_BY_MATERIAL[ligature as IconName];
  }
  if (ligature in PHOSPHOR_BY_ESCAPE) {
    return PHOSPHOR_BY_ESCAPE[ligature as keyof typeof PHOSPHOR_BY_ESCAPE];
  }
  const kebab = phosphorNameFor(ligature);
  if (!kebab) return undefined;
  const match = (Object.keys(PHOSPHOR_BY_MATERIAL) as IconName[]).find(
    (name) => phosphorNameFor(name) === kebab,
  );
  return match ? PHOSPHOR_BY_MATERIAL[match] : undefined;
}

function PhosphorGlyph({
  name,
  px,
  filled,
  label,
  className,
  style,
  ...rest
}: BoxProps & {
  name: string;
  px: number;
  filled: boolean;
  label?: string;
}) {
  const Glyph = phosphorComponent(name);
  if (!Glyph) return null;
  const boxStyle: CSSProperties = {
    width: px,
    height: px,
    ...style,
  };
  return (
    <IconBox label={label} className={className} set="phosphor" style={boxStyle} {...rest}>
      <Glyph
        size={px}
        weight={phosphorWeight(px, filled)}
        color="currentColor"
        mirrored={false}
        aria-hidden
      />
    </IconBox>
  );
}

export function Icon({
  name,
  size = "md",
  filled = false,
  label,
  set: setProp,
  className,
  style,
  ...rest
}: IconProps) {
  const px = resolvePx(size);
  const dark = useDarkClass();
  const inherited = useIconSet();
  const set = setProp ?? inherited;

  if (set === "phosphor") {
    const glyph = phosphorComponent(name);
    if (glyph) {
      return (
        <PhosphorGlyph
          name={name}
          px={px}
          filled={filled}
          label={label}
          className={className}
          style={style}
          {...rest}
        />
      );
    }
    return (
      <MaterialGlyph
        name={name}
        px={px}
        filled={filled}
        dark={dark}
        label={label}
        className={className}
        style={style}
        set="phosphor"
        fallback
        {...rest}
      />
    );
  }

  return (
    <MaterialGlyph
      name={name}
      px={px}
      filled={filled}
      dark={dark}
      label={label}
      className={className}
      style={style}
      set="material"
      {...rest}
    />
  );
}

export type { IconName } from "./icon-names";
export { ICON_NAMES, ICON_NAME_SET, MICRO_ICON_ALLOWLIST } from "./icon-names";
export { iconAxes } from "./icon-axes";
export { LUCIDE_TO_ICON, lucideToIcon } from "./lucide-to-icon";
export type { LucideIconName } from "./lucide-to-icon";
export { IconSetProvider, useIconSet, isIconSet, ICON_SETS } from "./icon-set";
export type { IconSet } from "./icon-set";
export { MATERIAL_TO_PHOSPHOR, phosphorNameFor } from "./material-to-phosphor";
export { phosphorWeight } from "./phosphor-weight";

/** Catalog ligature, or a host component (`className` slot — Lucide still compiles). */
export type IconGlyph = IconName | ComponentType<{ className?: string }>;

export function isIconLigature(glyph: IconGlyph): glyph is IconName {
  return typeof glyph === "string";
}

export function renderIconGlyph(
  glyph: IconGlyph,
  {
    size = "sm",
    className,
    filled,
  }: {
    size?: IconSize;
    className?: string;
    filled?: boolean;
  } = {},
): ReactNode {
  if (isIconLigature(glyph)) {
    return <Icon name={glyph} size={size} filled={filled} className={className} />;
  }
  const Slot = glyph;
  return <Slot className={className ?? "size-3.5 shrink-0"} aria-hidden />;
}
